import React, { useCallback, useEffect, useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import useApiTokensManagement from '../hooks/useApiTokensManagement.js';
import LoadingView from '../../shared/components/LoadingView';
import ApiTokensTable from '../components/apiTokens/ApiTokensTable.jsx';
import CreateApiTokenModal from '../components/apiTokens/CreateApiTokenModal.jsx';
import ApiTokenRevealModal from '../components/apiTokens/ApiTokenRevealModal.jsx';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

export default function OrganizationApiTokensPage() {
  useDocumentTitle('API tokens – Organization settings – Maintella');

  const {
    tokens,
    loading,
    error,
    actionError,
    setActionError,
    createToken,
    revokeToken,
  } = useApiTokensManagement();

  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const loadUsers = useCallback(async () => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/users`);
    if (res.ok) {
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreate = async (payload) => {
    setCreating(true);
    setActionError('');
    try {
      const data = await createToken(payload);
      setShowCreate(false);
      setRevealedToken(data.token);
    } catch {
      // surfaced via actionError
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token) => {
    if (!window.confirm(`Revoke API token "${token.name}"? Integrations using it will stop working.`)) {
      return;
    }
    setRevokingId(token.id);
    setActionError('');
    try {
      await revokeToken(token.id);
    } catch {
      // surfaced via actionError
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <ObjectTopBar
        title="API tokens"
        primaryBtn={{
          btnText: 'Create token',
          btnIcon: 'bi-plus-lg',
          onClick: () => setShowCreate(true),
        }}
      />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Machine-to-machine access</h6>
          <p className="text-muted small mb-0">
            Create long-lived API tokens for external systems. Tokens are exchanged for short-lived
            access tokens via POST /v1/oauth/api_token.
          </p>
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error.message || 'Failed to load API tokens.'}
          </div>
        )}

        {actionError && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {actionError}
          </div>
        )}

        {loading ? (
          <LoadingView
            symbolClass="bi-key"
            title="Loading API tokens"
            subtitle="Fetching the tokens issued for your organization."
            className="py-5"
          />
        ) : (
          <ApiTokensTable items={tokens} onRevoke={handleRevoke} revokingId={revokingId} />
        )}
      </div>

      <CreateApiTokenModal
        show={showCreate}
        users={users}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        creating={creating}
      />

      <ApiTokenRevealModal
        show={Boolean(revealedToken)}
        token={revealedToken}
        onClose={() => setRevealedToken(null)}
      />
    </>
  );
}
